/**
 * @file index.tsx
 *
 * @fileoverview Renders job alert modal and confirmation
 * dialogue or job alert form within.
 */

import { useContext } from 'react';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { isApplicant } from '~/jobiqo/lib/auth';
import JobAlertConfirm from '~/jobiqo/components/JobAlert/Confirm';
import JobAlertForm from '~/jobiqo/components/JobAlert/Form';
import { getQueryConditions } from '~/jobiqo/components/Search/utils/searchHelpers';
import { useGdprConsentContext } from '~/jobiqo/components/GDPRConsent/hooks/useGdprConsentContext';
import { AppContext } from '~/jobiqo/context/app';
import { AppContextInterface } from '~/jobiqo/models/app';
import {
  JobAlertContext,
  JobAlertContextInterface,
  updateCache,
} from '~/jobiqo/components/JobAlert/context';
import { Modal } from '~/vcl/atoms/15-modal';

export interface JobAlertModalProps {
  /**
   * The on close modal callback.
   */
  onClose?: () => void;
  /**
   * The on submit modal callback.
   */
  onSubmit?: () => void;
}

export default function JobAlertModal({
  onClose,
  onSubmit,
}: JobAlertModalProps) {
  const { user }: UserContextInterface = useContext(UserContext);
  const gdprConsentContext = useGdprConsentContext();
  const {
    querystring,
    showModal,
    jobAlert,
    jobAlertTitle,
    updateJobAlert,
    setShowModal,
  }: JobAlertContextInterface = useContext(JobAlertContext);
  const { locale }: AppContextInterface = useContext(AppContext);
  const isUserApplicant = user && isApplicant(user);

  const handleUpdateJobAlert = () => {
    onSubmit?.();
    if (isUserApplicant) {
      updateJobAlert();
    }
  };

  const handleClose = () => {
    setShowModal(false);
    onClose?.();
  };

  return isUserApplicant ? (
    <JobAlertConfirm
      showAlert={showModal}
      onConfirm={handleUpdateJobAlert}
      showCreate={!jobAlert}
      onClose={handleClose}
      user={user}
    />
  ) : (
    <Modal
      id="job_search_modal"
      showClose={true}
      closeTitle="Close"
      isShown={showModal}
      onHide={handleClose}
      maxWidth="550px"
      padding={[0, 11]}
    >
      <JobAlertForm
        gdprConsentContext={gdprConsentContext}
        jobAlertTitle={jobAlertTitle}
        onSubmit={(values) => {
          updateJobAlert({
            variables: {
              data: {
                name: jobAlertTitle,
                email: values.mail,
                // Only send an interval when the user actually picked one, an
                // explicit interval opts the alert out of the adaptive email
                // frequency.
                ...(values.notify_interval !== undefined && {
                  interval: Number(values.notify_interval),
                }),
                language: locale,
                condition_group: {
                  conjunction: 'AND',
                  groups: getQueryConditions(querystring),
                },
              },
            },
            update(cache) {
              // Update job alert related cache entries.
              updateCache(cache);
            },
          }).then(({ data }: any) => {
            setShowModal(false);
            onSubmit?.();
            if (data?.createJobAlert?.errors.length === 0) {
              gdprConsentContext.createGdprUserConsent(values.mail);
            }
          });
        }}
        onCancel={handleClose}
      />
    </Modal>
  );
}
